import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { 
  getTemplates, 
  addTemplate as addTemplateFirebase,
  updateTemplate as updateTemplateFirebase,
  deleteTemplate as deleteTemplateFirebase 
} from '../firebase/templates';

/**
 * Hook personalizado para gerenciar templates de WhatsApp
 * Separa a lógica de negócio da interface do usuário
 * 
 * @param {string} salonId - ID do salão atual
 * @returns {Object} Métodos e estados para gerenciar templates
 */
export const useWhatsAppTemplates = (salonId) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Função para carregar templates do Firestore
  const loadTemplates = useCallback(async () => {
    if (!salonId) return;
    
    try {
      setLoading(true);
      const templatesRef = collection(db, `salons/${salonId}/whatsappTemplates`);
      const templatesSnapshot = await getDocs(templatesRef);
      
      const templatesData = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTemplates(templatesData);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
      setError('Não foi possível carregar os templates. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);
  
  // Carregar templates ao inicializar o hook
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);
  
  // Adicionar novo template
  const addTemplate = useCallback(async (templateData) => {
    if (!salonId) return false;
    
    try {
      const success = await addTemplateFirebase(salonId, templateData);
      if (success) {
        await loadTemplates();
      }
      return success;
    } catch (err) {
      console.error('Erro ao adicionar template:', err);
      setError('Não foi possível adicionar o template. Por favor, tente novamente.');
      return false;
    }
  }, [salonId, loadTemplates]);
  
  // Atualizar template existente
  const updateTemplate = useCallback(async (templateId, templateData) => {
    if (!salonId) return false;
    
    try {
      const success = await updateTemplateFirebase(salonId, templateId, templateData);
      if (success) {
        await loadTemplates();
      }
      return success;
    } catch (err) {
      console.error('Erro ao atualizar template:', err);
      setError('Não foi possível atualizar o template. Por favor, tente novamente.');
      return false;
    }
  }, [salonId, loadTemplates]);
  
  // Excluir template
  const deleteTemplate = useCallback(async (templateId) => {
    if (!salonId) return false;
    
    try {
      const success = await deleteTemplateFirebase(salonId, templateId);
      if (success) {
        await loadTemplates();
      }
      return success;
    } catch (err) {
      console.error('Erro ao excluir template:', err);
      setError('Não foi possível excluir o template. Por favor, tente novamente.');
      return false;
    }
  }, [salonId, loadTemplates]);
  
  /**
   * Função para gerar links para ações do agendamento
   * @param {string} appointmentId - ID do agendamento
   * @param {string} token - Token de acesso
   * @returns {object} Objeto com os links de ação
   */
  const getAppointmentLinks = useCallback((appointmentId, token) => {
    if (!appointmentId || !token) {
      return {
        confirm: '#link-indisponivel',
        reschedule: '#link-indisponivel',
        cancel: '#link-indisponivel'
      };
    }
    
    // Obter URL base da aplicação
    const baseUrl = window.location.origin;
    
    return {
      confirm: `${baseUrl}/appointment/confirm/${appointmentId}?token=${token}`,
      reschedule: `${baseUrl}/appointment/reschedule/${appointmentId}?token=${token}`,
      cancel: `${baseUrl}/appointment/cancel/${appointmentId}?token=${token}`
    };
  }, []);

  /**
   * Processar template substituindo variáveis por valores
   * @param {string} template - Template com variáveis
   * @param {object} data - Objeto com valores para substituir variáveis
   * @returns {string} Template processado
   */
  const processTemplate = useCallback((template, data = {}) => {
    if (!template) return '';
    
    let processedTemplate = template;
    
    // Substituir variáveis padrão
    const variables = {
      cliente: data.cliente || 'Cliente',
      data: data.data || 'Data',
      hora: data.hora || 'Horário',
      servico: data.servico || 'Serviço',
      profissional: data.profissional || 'Profissional',
      valor: data.valor || 'Valor',
      salao: data.salao || 'Salão',
      telefone: data.telefone || 'Telefone',
      email: data.email || ''
    };
    
    // Substituir variáveis no template
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      processedTemplate = processedTemplate.replace(regex, value);
    });
    
    // Adicionar links dinâmicos se o appointmentId e token estiverem disponíveis
    if (data.id && data.accessToken) {
      const links = getAppointmentLinks(data.id, data.accessToken);
      
      processedTemplate = processedTemplate
        .replace(/\{link_confirmar\}/g, links.confirm)
        .replace(/\{link_reagendar\}/g, links.reschedule)
        .replace(/\{link_cancelar\}/g, links.cancel);
    }
    
    return processedTemplate;
  }, [getAppointmentLinks]);
  
  // Encontrar template padrão por tipo
  const getDefaultTemplateForType = useCallback((type) => {
    if (!templates.length) return null;
    
    // Procurar template padrão para o tipo
    const defaultTemplate = templates.find(t => t.type === type && t.isDefault);
    
    // Se não encontrar padrão, pegar o primeiro do tipo
    if (!defaultTemplate) {
      return templates.find(t => t.type === type) || null;
    }
    
    return defaultTemplate;
  }, [templates]);

  return {
    templates,
    loading,
    error,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    processTemplate,
    getDefaultTemplateForType,
    getAppointmentLinks,
    refreshTemplates: loadTemplates
  };
};

export default useWhatsAppTemplates; 